import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, HostBinding, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, filter, startWith } from 'rxjs';

import { Animations } from 'src/app/animations/animations';
import { AnalyticsEvent } from 'src/app/analytics/analytics-event';
import { AnalyticEventsLabels } from 'src/app/analytics/analytics-events-labels';
import { AnalyticsService } from 'src/app/analytics/analytics.service';
import { EventActionType } from 'src/app/analytics/event-action.enum';
import { FeatureFlagQuery } from 'src/app/feature-flag/state/feature-flag.query';
import { PermissionsService } from 'src/app/auth/permissions.service';
import { RolePermissionsHelper } from 'src/app/auth/role-permmissions-helper';
import { RoutesManager } from 'src/app/shared/models/routes.interfaces';
import { AiPostureAccessService } from 'src/app/ai-posture/guards/ai-posture-access.service';
import { RoleEnum } from '../models/permission-role.enum';
import { NavMenuItem } from '../app-sidenav-content/app-sidenav-content.interface';
import { MenuItemId } from '../app-sidenav-content/menu-item.enum';
import { AppSidenavPimpleService } from '../app-sidenav-content/app-sidenav-pimple.service';
import { resolveNavIcon } from './nav-icon.map';
import { NavMenuItemsService } from './nav-menu-items.service';

/**
 * CMS-driven menu for the new sidenav (`DevNewSideNav`). Reads the `nav-menu-v3`
 * content item, falling back to the existing nav-menu content until `nav-menu-v3`
 * exists in Squidex. Reuses the `NavMenuItem` model, `MenuItemId`, role filtering,
 * and pimple/badge service from the old sidenav during the rollout phase.
 */
@Component({
    standalone: false,
    selector: 'app-nav-content',
    templateUrl: './app-nav-content.component.html',
    styleUrls: ['./app-nav-content.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [Animations.pimple],
})
export class AppNavContentComponent implements OnInit {
    @HostBinding('class.shrink')
    @Input()
    shrink = false;

    items: NavMenuItem[] = [];
    pimpleMap = new Map<string, Observable<number>>();

    /** Current URL sans query/hash — drives leaf active state (OnPush needs an explicit trigger). */
    private activeUrl = '';

    readonly tpProps = { placement: 'right' } as const;

    constructor(
        private featureFlagQuery: FeatureFlagQuery,
        private router: Router,
        private analytics: AnalyticsService,
        private pimpleService: AppSidenavPimpleService,
        private permissionsService: PermissionsService,
        private navMenuItemsService: NavMenuItemsService,
        private aiPostureAccess: AiPostureAccessService,
        private cdr: ChangeDetectorRef,
        private destroyRef: DestroyRef,
    ) {}

    ngOnInit(): void {
        this.initItems();
        this.trackActiveUrl();
    }

    /**
     * Leaf active state, replacing `routerLinkActive`: the link's `routerLink` can differ from the
     * settled URL after a redirect (e.g. `/processing-activities` → `/ropa-assessments`), so match
     * the current URL against the item's route AND its known redirect alias.
     */
    isLeafActive(item: NavMenuItem): boolean {
        return this.routeMatches(item.route) || this.routeMatches(this.routeAlias(item.route));
    }

    private trackActiveUrl(): void {
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            startWith(null),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(() => {
            this.activeUrl = this.router.url.split(/[?#]/)[0];
            this.cdr.markForCheck();
        });
    }

    private routeMatches(route: string | undefined): boolean {
        if (!route) {
            return false;
        }
        const base = `/${route.replace(/^\//, '')}`;
        return this.activeUrl === base || this.activeUrl.startsWith(`${base}/`);
    }

    /** `/processing-activities` redirects to the RoPA assessments page — keep the nav item active there. */
    private routeAlias(route: string | undefined): string | undefined {
        return route === RoutesManager.processingActivities ? RoutesManager.ropa_assessments : undefined;
    }

    /** New sidenav icon name (`<i mine-icon>`); null falls back to the legacy CMS sprite icon. */
    iconName(item: NavMenuItem): string | null {
        return resolveNavIcon(item.id, item.icon);
    }

    onItemClick(item: NavMenuItem): void {
        // Section open/close is owned by mine-expansion-panel (its own header toggle);
        // item.active is synced back via onSectionToggle for the chevron. Here we only log.
        this.logAnalyticsEvent(item);
    }

    /** Sync section open state back for the chevron; markForCheck since OnPush + in-place mutation. */
    onSectionToggle(item: NavMenuItem, isOpen: boolean): void {
        item.active = isOpen;
        this.cdr.markForCheck();
    }

    private initItems(): void {
        const visibleItems = (this.resolveMenuItems() ?? [])
            .filter(item => this.checkFeatureFlag(item))
            .filter(item => this.checkFeatureFlagRemove(item))
            .map(item => {
                this.checkPimple(item);
                item = this.checkSubitems(item);
                // Sections are expanded by default (Figma shows all nav items visible).
                return { ...item, active: !!item.subMenu?.length };
            })
            // Drop anything the current role can't see: sections with no permitted
            // items (else an empty header renders) and forbidden top-level leaf items.
            .filter(item => this.isItemVisible(item));

        // Figma (520-46736): "My tasks" is the last ungrouped item. Guarantee it in the new nav
        // (some CMS sources — stale backup / v3 — omit it) and move it to the end before the divider.
        this.ensureTasksAtEnd(visibleItems);

        // The divider sits between the sections and the trailing ungrouped items. Derive
        // it from the surviving items (the CMS `divider` flag can sit on a dropped section).
        this.items = this.applyGroupDivider(visibleItems);

        this.highlightActiveItem();
    }

    /**
     * A section (has `subMenu`) is visible when at least one sub-item is permitted for
     * the current role; a top-level leaf item is visible when its own route is permitted.
     */
    private isItemVisible(item: NavMenuItem): boolean {
        return item.subMenu?.length
            ? item.subMenu.some(sub => this.permissionsService.getIsAllowed(sub.permittedRoles ?? []))
            : this.permissionsService.getIsAllowed(item.permittedRoles ?? []);
    }

    /**
     * Guarantee "My tasks" as the last ungrouped item (Figma 520-46736). If the resolved CMS items
     * already contain it, move it to the end; otherwise inject a canonical item (the `nav-menu-v3`
     * content and the bundled backup can omit it). Role gating still applies via `*minePermissions`.
     */
    private ensureTasksAtEnd(items: NavMenuItem[]): void {
        const index = items.findIndex(item => item.id === MenuItemId.Tasks);
        const tasks = index > -1 ? items.splice(index, 1)[0] : this.buildTasksItem();
        items.push(tasks);
    }

    /** Canonical "My tasks" leaf, mirroring the `nav-menu` Squidex item (id/title/icon/route). */
    private buildTasksItem(): NavMenuItem {
        return {
            id: MenuItemId.Tasks,
            title: 'My tasks',
            icon: 'icon-tasks',
            route: RoutesManager.tasks,
            analyticsLabel: 'SIDEBAR_tasks',
            subMenu: [],
            active: false,
            featureFlagName: null,
            hideMenuItemFeatureFlag: null,
            permittedRoles: RolePermissionsHelper.getPerrmitedRoleByRoute([RoutesManager.tasks]),
        };
    }

    /** Draw one divider after the last section, but only if ungrouped items follow it. */
    private applyGroupDivider(items: NavMenuItem[]): NavMenuItem[] {
        const lastSectionIndex = items.reduce((last, item, i) => (item.subMenu?.length ? i : last), -1);
        const hasTrailingItems = lastSectionIndex >= 0 && lastSectionIndex < items.length - 1;
        return items.map((item, i) => ({ ...item, divider: hasTrailingItems && i === lastSectionIndex }));
    }

    /** `nav-menu-v3` when present; otherwise the existing nav-menu content (dev fallback). */
    private resolveMenuItems(): NavMenuItem[] {
        return this.navMenuItemsService.getItems();
    }

    private highlightActiveItem(): void {
        const activeUrl = this.router.url;
        let activeNavMenu = this.items.find(i => i.route && activeUrl.includes(i.route));
        if (!activeNavMenu) {
            activeNavMenu = this.items.find(i => i.subMenu?.length && i.subMenu?.find(subItem => activeUrl.includes(subItem?.route)));
        }
        if (activeNavMenu) {
            activeNavMenu.active = true;
        }
        this.cdr.markForCheck();
    }

    private checkSubitems(item: NavMenuItem): NavMenuItem {
        if (item.subMenu?.length) {
            const filtered = item.subMenu
                .filter(sub => this.checkFeatureFlag(sub))
                .filter(sub => this.checkFeatureFlagRemove(sub));
            // Register each sub-item's pimple once — not once per (sub-item × sub-item) pair.
            filtered.forEach(sub => this.checkPimple(sub));
            item = {
                ...item,
                permittedRoles: RolePermissionsHelper.getPerrmitedRolesByMenuId(item.id),
                subMenu: filtered.map(subNav => ({
                    ...subNav,
                    subItem: true,
                    permittedRoles: RolePermissionsHelper.getPerrmitedRoleByRoute(subNav.route?.split('/') ?? []),
                })),
            };
        } else {
            item = { ...item, permittedRoles: this.getPermittedRoleForMenuItem(item) };
        }
        return item;
    }

    private getPermittedRoleForMenuItem(item: NavMenuItem): RoleEnum[] {
        if (item.id === MenuItemId.DataSources) {
            return [RoleEnum.Admin, RoleEnum.Agent, RoleEnum.InventoryManager];
        }
        // Split on '/' like sub-items do: the helper switches on the FIRST segment, so a
        // multi-segment leaf (e.g. the DevNewSideNav Consent repoint to `cmp/websites`)
        // would otherwise match no case and fall through to the permissive default.
        return item.id === MenuItemId.Dashboard
            ? RolePermissionsHelper.getPerrmitedRolesByMenuId(item.id)
            : RolePermissionsHelper.getPerrmitedRoleByRoute(item.route?.split('/') ?? []);
    }

    private checkPimple(item: NavMenuItem): void {
        if (item.pimple) {
            this.pimpleMap.set(item.id, this.pimpleService.getPimpleData(item.id));
        }
    }

    private checkFeatureFlag(item: NavMenuItem): boolean {
        // "My tasks" is a permanent item in the new nav (Figma 520-46736), so it's not gated
        // by the CMS `devAgentTasks` flag here (the old nav keeps that gate). This component
        // only renders under DevNewSideNav, so the override is inherently new-nav-scoped.
        if (item.id === MenuItemId.Tasks) {
            return true;
        }
        // remove once the flag is removed in unleash prod + squidex
        if (!item.featureFlagName || item.featureFlagName === 'dataTypesClassification') {
            return true;
        }
        // AI posture follows the company's AISPM module (or the prototype embed), not a flag.
        if (item.featureFlagName === AiPostureAccessService.CmsGateName) {
            return this.aiPostureAccess.canView();
        }
        // Retired flags are no longer fetched (absent from the store) → their gate is open: the
        // feature rolled out 100%, so a CMS nav item still naming the dead flag must stay visible.
        return this.featureFlagQuery.isCmsGateOpen(item.featureFlagName);
    }

    private checkFeatureFlagRemove(item: NavMenuItem): boolean {
        if (!item.hideMenuItemFeatureFlag) {
            return true;
        }
        // "AI assets" holds AI posture's slot until the company's AISPM module is enabled.
        if (item.hideMenuItemFeatureFlag === AiPostureAccessService.CmsGateName) {
            return !this.aiPostureAccess.isAispmEnabled();
        }
        return !this.featureFlagQuery.getFlag(item.hideMenuItemFeatureFlag);
    }

    private logAnalyticsEvent(item: NavMenuItem): void {
        if (!item.analyticsLabel) {
            return;
        }
        this.analytics.track({
            eventCategory: AnalyticEventsLabels[item.analyticsLabel] ?? item.analyticsLabel,
            eventAction: EventActionType.Click,
        } as AnalyticsEvent);
    }
}
